import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  ArrowLeftRight,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Truck,
  Building2,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { itemTransfersApi, type ItemTransfer, type TransferStatus } from "@/lib/api/item-transfers";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { cn } from "@/lib/utils";

const statusConfig: Record<
  TransferStatus,
  { label: string; cls: string; icon: React.ElementType }
> = {
  PENDING: { label: "Pending", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  IN_TRANSIT: { label: "In Transit", cls: "bg-primary/10 text-primary", icon: Truck },
  COMPLETED: { label: "Completed", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-100 text-gray-500", icon: XCircle },
};

type StatusFilter = "all" | TransferStatus;

const AllTransfersPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [transfers, setTransfers] = useState<ItemTransfer[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [branchFilter, setBranchFilter] = useState("");
  const [noteTransfer, setNoteTransfer] = useState<ItemTransfer | null>(null);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transferData, branchData] = await Promise.all([
        itemTransfersApi.getAll(
          statusFilter !== "all" ? { status: statusFilter as TransferStatus } : {}
        ),
        branchesApi.getAll(true),
      ]);
      setTransfers(transferData);
      setBranches(branchData);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load transfers.");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (
    transfer: ItemTransfer,
    action: "approve" | "complete" | "cancel"
  ) => {
    try {
      if (action === "approve") await itemTransfersApi.approve(transfer.id);
      else if (action === "complete") await itemTransfersApi.complete(transfer.id);
      else await itemTransfersApi.cancel(transfer.id);
      toast.success(
        `Transfer ${action === "approve" ? "dispatched" : action === "complete" ? "completed" : "cancelled"}.`
      );
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Action failed.");
    }
  };

  const displayed = branchFilter
    ? transfers.filter((t) => t.fromBranchId === branchFilter || t.toBranchId === branchFilter)
    : transfers;

  const counts = {
    PENDING: transfers.filter((t) => t.status === "PENDING").length,
    IN_TRANSIT: transfers.filter((t) => t.status === "IN_TRANSIT").length,
    COMPLETED: transfers.filter((t) => t.status === "COMPLETED").length,
    CANCELLED: transfers.filter((t) => t.status === "CANCELLED").length,
  };

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Item Transfers"
          description="All inter-branch item movements across the institution"
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: "All Transfers" },
          ]}
          showSearch={false}
          titleSize="md"
          actions={
            <Button variant="outline" onClick={() => navigate("/setup/branches")} className="gap-2">
              <Building2 className="w-4 h-4" />
              Manage Branches
            </Button>
          }
        />

        {/* Summary row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(Object.entries(counts) as [TransferStatus, number][]).map(([status, count]) => {
            const cfg = statusConfig[status];
            return (
              <button
                key={status}
                onClick={() => setStatusFilter((f) => (f === status ? "all" : status))}
                className={cn(
                  "p-4 rounded-lg border text-left transition-all",
                  statusFilter === status
                    ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                    : "border-gray-200 bg-white hover:border-gray-300"
                )}
              >
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <div
                  className={cn(
                    "inline-flex items-center gap-1 text-xs font-medium mt-1 px-2 py-0.5 rounded-full",
                    cfg.cls
                  )}
                >
                  <cfg.icon className="w-3 h-3" />
                  {cfg.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 flex-wrap">
            {(["all", "PENDING", "IN_TRANSIT", "COMPLETED", "CANCELLED"] as StatusFilter[]).map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-full border transition-colors",
                    statusFilter === s
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  )}
                >
                  {s === "all" ? "All" : statusConfig[s as TransferStatus]?.label}
                </button>
              )
            )}
          </div>

          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="ml-auto rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <LoadingSpinner size="lg" />
          </div>
        ) : displayed.length === 0 ? (
          <Card className="p-12 text-center">
            <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No transfers found</p>
            <p className="text-sm text-gray-400 mt-1">
              Transfers can be requested from any branch's Transfers tab.
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Item</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden sm:table-cell">
                    Route
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">
                    Requested
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayed.map((t) => {
                  const cfg = statusConfig[t.status];
                  const StatusIcon = cfg.icon;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 truncate max-w-xs">
                          {t.item?.biblio?.title ?? "Unknown item"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t.item?.barcode ?? "—"} · Acc #{t.item?.accessionNumber ?? "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-xs">
                          <button
                            onClick={() => navigate(`/setup/branches/${t.fromBranchId}/transfers`)}
                            className="font-medium text-gray-700 hover:text-primary hover:underline"
                          >
                            {t.fromBranch?.name}
                          </button>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <button
                            onClick={() => navigate(`/setup/branches/${t.toBranchId}/transfers`)}
                            className="font-medium text-gray-700 hover:text-primary hover:underline"
                          >
                            {t.toBranch?.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
                            cfg.cls
                          )}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                        {new Date(t.requestedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end items-center">
                          {t.status === "PENDING" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs gap-1"
                                onClick={() => handleAction(t, "approve")}
                              >
                                <Truck className="w-3 h-3" /> Dispatch
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-xs text-red-500"
                                onClick={() => handleAction(t, "cancel")}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {t.status === "IN_TRANSIT" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1 text-green-700 border-green-300"
                              onClick={() => handleAction(t, "complete")}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Received
                            </Button>
                          )}
                          {t.notes && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs gap-1 text-gray-500 hover:text-gray-700"
                              onClick={() => setNoteTransfer(t)}
                              title="View Note"
                            >
                              <FileText className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </PageContainer>

      {/* ── View Note Modal ── */}
      <Dialog
        open={!!noteTransfer}
        onOpenChange={(v) => {
          if (!v) setNoteTransfer(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Transfer Note
            </DialogTitle>
            <DialogDescription>
              Note for "{noteTransfer?.item?.biblio?.title ?? "Unknown item"}" (
              {noteTransfer?.fromBranch?.name} → {noteTransfer?.toBranch?.name})
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
            {noteTransfer?.notes || "No note available."}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setNoteTransfer(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default AllTransfersPage;
